import IconButton from '@/global/components/Button/IconButton'
import { TextField, type TextFieldProps } from '@/global/components/TextField'
import { cn } from '@/lib/utils'
import { UploadCloudIcon, XIcon } from 'lucide-react'
import React, {
    Fragment,
    useImperativeHandle,
    useRef,
    useState,
    type ChangeEvent,
} from 'react'
import {
    Controller,
    useFormContext,
    type UseControllerProps,
} from 'react-hook-form'
import { DialogFile } from './DialogFile'

/** O campo de arquivo nunca é `multiline` — fixa o ramo `input` da união do `TextField`, senão `multiline` viraria obrigatório nos call-sites (a união exige o discriminante). */
type TextFieldInputProps = Extract<TextFieldProps, { multiline?: false }>

type FormsProps = Omit<UseControllerProps, 'control'> &
    Omit<
        TextFieldInputProps,
        'name' | 'value' | 'ref' | 'accept' | 'onChange'
    > & {
        name: string
        notPreview?: boolean
        unHelperText?: boolean
        clearable?: boolean
        accept?: File['type'] | Array<File['type']>
        /**
         * Avisa que o arquivo do campo mudou: o arquivo escolhido, ou `null`
         * quando o campo é limpo.
         *
         * Não é o `onChange` do `<input>` de propósito — ao limpar não existe
         * `ChangeEvent` algum (o gatilho é o clique no X), e era esse buraco
         * que o `as any` tapava, entregando um `MouseEvent` a quem esperava um
         * `ChangeEvent<HTMLInputElement>`.
         */
        onChange?: (arquivo: File | null) => void
        /**
         * Roda antes de abrir o seletor de arquivos. Retornar `false` impede a
         * abertura — use para exibir um aviso e só então chamar `abrirSeletor`
         * pela ref do campo. Quando bloqueia, o `onClick` também não é chamado.
         */
        onBeforeOpen?: () => boolean
    }

export interface FormFileHandle {
    /** Abre o seletor de arquivos sem passar por `onBeforeOpen`. */
    abrirSeletor: () => void
}

/**
 * Campo de arquivo ligado ao react-hook-form: o `TextField` mostra o nome do
 * arquivo escolhido e o `<input type="file">` fica escondido atrás do ícone.
 *
 * O valor gravado é o `File`, ou a string que a API devolveu quando o arquivo já
 * está salvo — daí `notPreview`/`clearable` controlarem o que aparece à direita.
 * O `onChange` do call-site recebe o arquivo (ou `null` ao limpar), não um
 * `ChangeEvent`: ao limpar não existe evento de input algum.
 */
export const FormFile = React.forwardRef<FormFileHandle, FormsProps>(
    function FormFile(
        {
            name,
            rules,
            onClick,
            onChange,
            disabled: disableFile,
            size = 'medium',
            variant = 'filled',
            defaultValue,
            notPreview,
            unHelperText,
            clearable,
            accept,
            helperText,
            onBeforeOpen,
            ...rest
        },
        ref,
    ) {
        const placeholder = disableFile
            ? ''
            : (defaultValue ?? 'Selecione um arquivo')

        const { control, setValue, setError, clearErrors } = useFormContext()
        const disabled = disableFile || (control as any)._options?.disabled

        const [fileName, setFileName] = useState(placeholder)
        const [url, setUrl] = useState<string | null>(null)
        const [openDialog, setOpenDialog] = useState(false)

        const refInputArquivo = useRef<HTMLInputElement>(null)

        const nomeDoValor = (value: string | File) =>
            value instanceof File ? value.name : (value ?? '')

        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            if (disabled) return
            if (!event.target.files) return
            const fileUploaded = event.target.files[0]

            if (accept) {
                const acceptArray = Array.isArray(accept) ? accept : [accept]
                if (!acceptArray.includes(fileUploaded.type)) {
                    return setError(name, {
                        type: 'accept',
                        message: `Tipo de arquivo não permitido. Permitido: ${acceptArray.join(
                            ', ',
                        )}`,
                    })
                }
            }

            clearErrors(name)
            setUrl(URL.createObjectURL(fileUploaded))
            setFileName(fileUploaded.name)
            setValue(name, fileUploaded)
            onChange?.(fileUploaded)
        }

        const abrirSeletor = () => {
            refInputArquivo.current?.click()
        }

        useImperativeHandle(ref, () => ({ abrirSeletor }), [])

        /**
         * Abre o seletor de arquivo. Fica no root do campo (ver `onClick` em
         * `ui/input`), então vale para o ícone de upload, o rótulo e a sobra da
         * caixa — não só para a faixa do controle.
         */
        const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
            if (disabled) return
            if (onBeforeOpen?.() === false) return

            abrirSeletor()
            onClick?.(event)
        }

        return (
            <Fragment>
                <input
                    type="file"
                    ref={refInputArquivo}
                    onChange={handleChange}
                    className="hidden"
                />
                <Controller
                    name={name}
                    rules={rules}
                    control={control}
                    render={({
                        field: { value, ...field },
                        fieldState: { error },
                    }) => {
                        const uploadUrl = value
                            ? `${import.meta.env.VITE_BASE_URL}/v1/uploads/${value}`
                            : undefined

                        const limpar = (
                            event: React.MouseEvent<HTMLElement>,
                        ) => {
                            event.preventDefault()
                            // O clique no X nasce dentro do campo, e o campo abre o
                            // seletor de arquivo no clique — sem barrar a
                            // propagação, limpar reabriria o seletor.
                            event.stopPropagation()
                            setValue(name, '')
                            setUrl(null)
                            // O valor exibido cai para o `fileName` quando o
                            // formulário fica vazio; sem repor o placeholder, o
                            // nome do arquivo removido continuaria escrito.
                            setFileName(placeholder)
                            if (refInputArquivo.current?.value) {
                                refInputArquivo.current.value = ''
                            }
                            onChange?.(null)
                        }

                        const miniatura = value && !notPreview && (
                            <img
                                src={url ?? uploadUrl}
                                alt="Pré-visualização do arquivo"
                                className={cn(
                                    'size-8 object-cover cursor-default',
                                    'border-foreground border rounded-xs',
                                    {
                                        'cursor-pointer': disabled,
                                    },
                                )}
                                onClick={event => {
                                    // Mesmo motivo do X: abrir a pré-visualização
                                    // não pode também abrir o seletor de arquivo.
                                    event.stopPropagation()
                                    setOpenDialog(prev => !prev)
                                }}
                            />
                        )

                        const botaoLimpar = value && clearable && (
                            <IconButton
                                title="Limpar"
                                size="small"
                                onClick={limpar}
                            >
                                <XIcon />
                            </IconButton>
                        )

                        /**
                         * `undefined` quando não há nada a mostrar — um fragmento
                         * vazio ainda renderiza o `<span>` do adorno no `ui/input`,
                         * que consome o `gap-2` e desloca o valor do campo.
                         */
                        const adornoFinal =
                            miniatura || botaoLimpar ? (
                                <div className="inline-flex gap-2 mb-3">
                                    {miniatura}
                                    {botaoLimpar}
                                </div>
                            ) : undefined

                        return (
                            <>
                                <TextField
                                    {...rest}
                                    {...field}
                                    readOnly
                                    disabled={disabled}
                                    size={size}
                                    variant={variant}
                                    onClick={handleClick}
                                    className="[&_input]:cursor-pointer"
                                    value={nomeDoValor(value) || fileName}
                                    data-testid={name}
                                    startAdornment={<UploadCloudIcon />}
                                    endAdornment={adornoFinal}
                                    unHelperText={unHelperText}
                                    error={error?.message}
                                    helperText={helperText}
                                />
                                <DialogFile
                                    file={value}
                                    open={openDialog}
                                    onClose={() => setOpenDialog(false)}
                                />
                            </>
                        )
                    }}
                />
            </Fragment>
        )
    },
)
